import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { KycService } from 'src/app/views/settings/kyc/kyc.service';
import { BuyerKycService } from 'src/app/views/settings/kyc/buyer-kyc.service';
import { ComplianceExemptionService } from './compliance-exemption.service';
import { gatePassed } from '../utils/compliance-exemption.util';

export type KycStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'CLARIFICATION_REQUIRED'
  | 'APPROVED'
  | 'REJECTED';

export type KycLoadState = 'IDLE' | 'LOADING' | 'SUCCESS' | 'ERROR';

export interface KycStatusSnapshot {
  loadState: KycLoadState;
  status: KycStatus;
  finalDecision: 'APPROVED' | 'REJECTED' | null;
  kycId: number | null;
  kycReferenceNumber: string | null;
  completionPercent: number;
  // Last refresh timestamp so callers can decide whether to force-refresh.
  fetchedAt: number;
  // Company compliance exemption: KYC gate is skipped while true. status stays the TRUE status.
  exempt: boolean;
  exemptUntil: string | null;
}

const NOT_STARTED_SNAPSHOT: KycStatusSnapshot = {
  loadState: 'IDLE',
  status: 'NOT_STARTED',
  finalDecision: null,
  kycId: null,
  kycReferenceNumber: null,
  completionPercent: 0,
  fetchedAt: 0,
  exempt: false,
  exemptUntil: null,
};

/**
 * Singleton holding the current user's KYC status. Sidebar, route guard,
 * dashboard banner and modal all subscribe so the gating stays consistent
 * whenever the status changes (e.g. after submit, or after admin approval).
 */
@Injectable({ providedIn: 'root' })
export class KycStatusService {
  private readonly subject$ = new BehaviorSubject<KycStatusSnapshot>(NOT_STARTED_SNAPSHOT);
  private inflight: Promise<KycStatusSnapshot> | null = null;

  readonly status$: Observable<KycStatusSnapshot> = this.subject$.asObservable();

  constructor(
    private cookies: CookieService,
    private supplierKyc: KycService,
    private buyerKyc: BuyerKycService,
    private exemptionApi: ComplianceExemptionService
  ) {}

  get snapshot(): KycStatusSnapshot {
    return this.subject$.getValue();
  }

  /** Admin users are not gated — return a synthetic APPROVED snapshot. */
  isApplicantRole(): boolean {
    const role = (this.cookies.get('userRole') || '').trim().toLowerCase();
    return role === 'suppliers' || role === 'owner';
  }

  isAdmin(): boolean {
    const role = (this.cookies.get('userRole') || '').trim().toLowerCase();
    return role === 'admin';
  }

  isApproved(): boolean {
    if (!this.isApplicantRole()) {
      return true;
    }
    return gatePassed(this.subject$.getValue());
  }

  /**
   * Fetch the latest KYC dashboard row for the current user and broadcast
   * the resulting status. Multiple concurrent calls share one in-flight
   * request so a guard + sidebar + dashboard mounting at the same time
   * doesn't hit the backend three times.
   */
  /** Skip a refetch if the last refresh completed within this window.
   *  Prevents a 3-segment route navigation (canActivate + 2x canActivateChild)
   *  from triggering 3 separate /buyer/kyc/dashboard XHRs when each one
   *  fires AFTER the previous request's promise resolves (so the inflight
   *  dedup misses). The 30s window is generous for guard chains but short
   *  enough that admin status changes are picked up quickly. */
  private readonly recencyMs = 30_000;

  refresh(force = false): Promise<KycStatusSnapshot> {
    if (!force) {
      if (this.inflight) {
        return this.inflight;
      }
      const snap = this.subject$.getValue();
      if (snap.loadState === 'SUCCESS' && snap.fetchedAt && Date.now() - snap.fetchedAt < this.recencyMs) {
        // Recent enough — skip the network call, reuse the cached snapshot.
        return Promise.resolve(snap);
      }
    }

    if (this.isAdmin()) {
      const adminSnap: KycStatusSnapshot = {
        loadState: 'SUCCESS',
        status: 'APPROVED',
        finalDecision: 'APPROVED',
        kycId: null,
        kycReferenceNumber: null,
        completionPercent: 100,
        fetchedAt: Date.now(),
        exempt: false,
        exemptUntil: null,
      };
      this.subject$.next(adminSnap);
      return Promise.resolve(adminSnap);
    }

    const userIdRaw = this.cookies.get('userId');
    const userId = userIdRaw ? Number(userIdRaw) : 0;
    if (!userId) {
      console.warn('[KYC-Status] no userId cookie — cannot fetch status');
      const errorSnap: KycStatusSnapshot = {
        ...this.subject$.getValue(),
        loadState: 'ERROR',
      };
      this.subject$.next(errorSnap);
      return Promise.resolve(errorSnap);
    }

    this.subject$.next({ ...this.subject$.getValue(), loadState: 'LOADING' });

    const role = (this.cookies.get('userRole') || '').trim().toLowerCase();
    console.log(
      '%c[KYC-Status] ▶ Fetching dashboard',
      'color:#1f826f;font-weight:bold;',
      { userId, role, endpoint: role === 'owner' ? '/buyer/kyc/dashboard' : '/supplier/kyc/dashboard' }
    );
    console.log('[KYC-Status] ⚠️ This userId is what the SP filters by — supplierKycMaster.supplierId must equal:', userId);

    const fetcher =
      role === 'owner'
        ? this.buyerKyc.getDashboard(userId)
        : this.supplierKyc.getDashboard(userId);

    // No timeout, no race, no guessing. We simply WAIT for the real status.
    //
    // The fetch always settles: on success we get the true status, while an
    // error is explicitly marked non-authoritative below. The old 6s race
    // was the actual source of the intermittent-modal bug: on a slow load
    // the timeout won,
    // broadcast a fabricated NOT_STARTED, popped the KYC modal for an
    // already-APPROVED user, and discarded the real APPROVED result. By
    // waiting for the real answer, nobody — guard, sidebar, or modal — ever
    // decides anything on a placeholder status.
    const exemptionP = this.exemptionApi.fetchMine(); // never rejects
    this.inflight = fetcher
      .then((resp: any) => {
        // WebApiService.getRequest unwraps the backend's `{ data: { rows } }`
        // envelope and returns either the array directly OR `{ rows: [...] }`
        // depending on what came back. Normalize defensively — same pattern
        // SupplierKycComponent.loadDashboard already uses.
        const rows: any[] = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.rows)
          ? resp.rows
          : Array.isArray(resp?.data?.rows)
          ? resp.data.rows
          : [];
        return this.buildSnapshotFromRows(rows);
      })
      .then(async (snap) => {
        const ex = await exemptionP;
        return { ...snap, exempt: !!ex?.skipKyc, exemptUntil: ex?.endsAt ?? null };
      })
      .catch(() => {
        // An API/authentication failure is not a KYC decision. Preserve the
        // last known data, but mark it as non-authoritative so no UI can open
        // the KYC modal or show NOT_STARTED based on the failed request.
        return { ...this.subject$.getValue(), loadState: 'ERROR' as KycLoadState };
      })
      .then((snap) => {
        // Publish the result to every subscriber. Consumers only make KYC
        // decisions when loadState is SUCCESS.
        this.subject$.next(snap);
        return snap;
      })
      .finally(() => {
        this.inflight = null;
      });

    return this.inflight;
  }

  /** Allow components that just submitted/started KYC to push the new status without a refetch. */
  setStatus(status: KycStatus, kycId?: number | null, kycReferenceNumber?: string | null) {
    const normalizedStatus = this.normalizeStatus(status);
    this.subject$.next({
      loadState: 'SUCCESS',
      status: normalizedStatus,
      finalDecision: this.finalDecisionForStatus(normalizedStatus),
      kycId: kycId ?? this.subject$.getValue().kycId,
      kycReferenceNumber: kycReferenceNumber ?? this.subject$.getValue().kycReferenceNumber,
      completionPercent: this.completionPercentForStatus(normalizedStatus),
      fetchedAt: Date.now(),
      exempt: this.subject$.getValue().exempt,
      exemptUntil: this.subject$.getValue().exemptUntil,
    });
  }

  /** Reset on logout so a different user landing on the same browser session doesn't see stale status. */
  reset() {
    this.subject$.next(NOT_STARTED_SNAPSHOT);
    this.inflight = null;
    this.exemptionApi.clearMine();
  }

  private buildSnapshotFromRows(rows: any[]): KycStatusSnapshot {
    if (!rows.length) {
      return { ...NOT_STARTED_SNAPSHOT, loadState: 'SUCCESS', fetchedAt: Date.now() };
    }

    const normalizedRows = rows.map((row) => ({
      row,
      status: this.normalizeStatus(row?.status),
      finalDecision: this.finalDecisionForRow(row),
      completionPercent: this.completionPercentForRow(row),
    }));

    // If any active dashboard row is fully approved, the account is verified.
    // This prevents a stale/newer non-approved row from reopening the post-login
    // KYC modal after admin approval.
    const approvedRow =
      normalizedRows.find((entry) => entry.finalDecision === 'APPROVED' && entry.completionPercent === 100) ||
      normalizedRows.find((entry) => entry.finalDecision === 'APPROVED');
    const selected = approvedRow || normalizedRows[0];

    return {
      loadState: 'SUCCESS',
      status: selected.status,
      finalDecision: selected.finalDecision,
      kycId: selected.row?.kycId ?? null,
      kycReferenceNumber: selected.row?.kycReferenceNumber ?? null,
      completionPercent: selected.completionPercent,
      fetchedAt: Date.now(),
      exempt: false,
      exemptUntil: null,
    };
  }

  private normalizeStatus(status: unknown): KycStatus {
    const normalized = String(status || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_') as KycStatus;

    switch (normalized) {
      case 'IN_PROGRESS':
      case 'SUBMITTED':
      case 'UNDER_REVIEW':
      case 'CLARIFICATION_REQUIRED':
      case 'APPROVED':
      case 'REJECTED':
        return normalized;
      case 'NOT_STARTED':
      default:
        return 'NOT_STARTED';
    }
  }

  private completionPercentForRow(row: any): number {
    const explicit =
      row?.completionPercent ??
      row?.kycCompletionPercent ??
      row?.kycProgress ??
      row?.progressPercent ??
      row?.progress;
    const parsed = Number(explicit);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.min(100, parsed));
    }
    return this.completionPercentForStatus(this.normalizeStatus(row?.status));
  }

  private completionPercentForStatus(status: KycStatus): number {
    switch (status) {
      case 'APPROVED':
        return 100;
      case 'REJECTED':
      case 'NOT_STARTED':
        return 0;
      case 'IN_PROGRESS':
        return 25;
      case 'SUBMITTED':
        return 75;
      case 'UNDER_REVIEW':
      case 'CLARIFICATION_REQUIRED':
        return 80;
      default:
        return 0;
    }
  }

  private finalDecisionForStatus(status: KycStatus): 'APPROVED' | 'REJECTED' | null {
    if (status === 'APPROVED' || status === 'REJECTED') {
      return status;
    }
    return null;
  }

  private finalDecisionForRow(row: any): 'APPROVED' | 'REJECTED' | null {
    const explicit = String(row?.finalDecision || row?.verificationStatus || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    if (explicit === 'APPROVED' || explicit === 'REJECTED') {
      return explicit;
    }
    return this.finalDecisionForStatus(this.normalizeStatus(row?.status));
  }
}
